import React, { useState, useEffect, useCallback, useRef } from 'react';
import { refreshApi, logoutApi } from './AuthService';

import { useAuthStore } from '../store/authStore';
import { SESSION } from '../config/sessionConfig';
import { logActivity } from '../services/ApiServices';
import { logActionType } from '../services/MyCollections';

function getTokenExpiry(token) {
  try {
    if (!token || token === 'null' || token === 'undefined' || !token.includes('.')) return null;
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const payload = JSON.parse(jsonPayload);
    return payload.exp ? payload.exp * 1000 : null;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.error("Token decode error:", e);
    }
    return null;
  }
}

function formatTime(ms) {
  if (ms <= 0) return '0:00';
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

function getInitialLastActivity() {
  try {
    const stored = localStorage.getItem('pelikan-last-activity');
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
  } catch (e) {}
  return Date.now();
}

const SessionTimeoutNotification = () => {
  const { getToken, getRefreshToken, updateAccessToken, isAuthenticated } = useAuthStore();
  const logoutStore = useAuthStore(state => state.logout);
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isExtending, setIsExtending] = useState(false);

  // Czas ostatniej aktywności inicjalizowany z localStorage (ochrona przed obejściem limitu 2h przez odświeżenie karty)
  const lastActivityRef = useRef(getInitialLastActivity());
  const lastStorageWriteRef = useRef(0);

  const getLastActivity = () => {
    try {
      const stored = localStorage.getItem('pelikan-last-activity');
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed > 0) {
          return Math.max(parsed, lastActivityRef.current);
        }
      }
    } catch (e) {}
    return lastActivityRef.current;
  };

  const doLogout = useCallback(async (isManual = false) => {
    const accessToken = getToken();
    const refreshToken = getRefreshToken();
    const userName = useAuthStore.getState().fullName;
    
    try {
      if (isManual) {
        await logActivity(logActionType.LOGOUT, userName, accessToken);
      }
      await logoutApi(accessToken, refreshToken).catch(() => {});
    } catch (e) {}
    
    logoutStore();
    sessionStorage.clear();
    localStorage.removeItem('pelikan-auth');
    localStorage.removeItem('pelikan-last-activity');
    localStorage.removeItem('pelikan-refresh-lock');
    
    window.location.href = isManual ? '/SignIn' : '/SessionExpired';
  }, [getToken, getRefreshToken, logoutStore]);

  const isExtendingRef = useRef(false);

  const handleExtend = useCallback(async () => {
    if (isExtendingRef.current) return;

    // Cross-tab lock: zapobiega równoczesnej rotacji tego samego refresh tokena przez kilka otwartych kart
    try {
      const lockTime = parseInt(localStorage.getItem('pelikan-refresh-lock') || '0', 10);
      if (Date.now() - lockTime < 10000) {
        return;
      }
      localStorage.setItem('pelikan-refresh-lock', Date.now().toString());
    } catch (e) {}

    isExtendingRef.current = true;
    setIsExtending(true);
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      try { localStorage.removeItem('pelikan-refresh-lock'); } catch (e) {}
      doLogout();
      setIsExtending(false);
      isExtendingRef.current = false;
      return;
    }

    const result = await refreshApi(refreshToken);
    if (result.isSuccess) {
      updateAccessToken(result.newAuthToken, result.newRefreshToken);
      setShowWarning(false);
    } else {
      try {
        useAuthStore.persist?.rehydrate?.();
      } catch (e) {}
      const latestRefreshToken = getRefreshToken();
      if (!result.isRateLimited && latestRefreshToken === refreshToken) {
        doLogout();
      }
    }
    try { localStorage.removeItem('pelikan-refresh-lock'); } catch (e) {}
    setIsExtending(false);
    isExtendingRef.current = false;
  }, [getRefreshToken, updateAccessToken, doLogout]);

  const handleKeepAlive = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    lastStorageWriteRef.current = now;
    try {
      localStorage.setItem('pelikan-last-activity', now.toString());
    } catch (e) {}
    setShowWarning(false);
    handleExtend();
  }, [handleExtend]);

  const statesRef = useRef({ showWarning, handleExtend, doLogout, getToken });
  useEffect(() => {
    statesRef.current = { showWarning, handleExtend, doLogout, getToken };
  }, [showWarning, handleExtend, doLogout, getToken]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const nowOnMount = Date.now();
    const initialLastActivity = getLastActivity();
    if (nowOnMount - initialLastActivity >= SESSION.IDLE_THRESHOLD_MS) {
      statesRef.current.doLogout();
      return;
    }

    try {
      if (!localStorage.getItem('pelikan-last-activity')) {
        localStorage.setItem('pelikan-last-activity', nowOnMount.toString());
      }
    } catch (e) {}

    const recordActivity = () => {
      // Gdy wyświetlane jest okno ostrzeżenia, wymagamy świadomego kliknięcia "Pozostań zalogowany"
      if (statesRef.current.showWarning) return;
      const now = Date.now();
      const currentLast = getLastActivity();
      if (now - currentLast >= SESSION.IDLE_THRESHOLD_MS) {
        return;
      }
      lastActivityRef.current = now;
      if (now - lastStorageWriteRef.current > 5000) {
        lastStorageWriteRef.current = now;
        try {
          localStorage.setItem('pelikan-last-activity', now.toString());
        } catch (e) {}
      }
    };

    window.addEventListener('mousemove', recordActivity);
    window.addEventListener('keydown', recordActivity);
    window.addEventListener('click', recordActivity);
    window.addEventListener('scroll', recordActivity);

    const interval = setInterval(() => {
      const now = Date.now();
      const lastActivity = getLastActivity();
      const idleTime = now - lastActivity;
      const idleRemaining = SESSION.IDLE_THRESHOLD_MS - idleTime;

      // 1. Sprawdzenie 2 godzin bezczynności
      if (idleRemaining <= 0) {
        if (process.env.NODE_ENV === 'development') {
          console.warn("[SessionTimeout] Przekroczono 2h bezczynności – wylogowanie.");
        }
        statesRef.current.doLogout();
        return;
      }

      // 2. Jeśli do limitu 2h zostało mniej niż 2 minuty – pokaż ostrzeżenie
      if (idleRemaining <= SESSION.WARNING_BEFORE_MS) {
        setTimeLeft(idleRemaining);
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }

      // 3. Cichy auto-refresh 35-minutowego Access Tokena w tle (dopóki użytkownik nie jest bezczynny 2h)
      const token = statesRef.current.getToken();
      if (!token) return;

      const expiry = getTokenExpiry(token);
      if (!expiry) return;

      const tokenRemaining = expiry - now;

      // Gdy access token wygasa za mniej niż 5 minut (lub wygasł w zamkniętej karcie/uśpieniu)
      const refreshThreshold = SESSION.TOKEN_REFRESH_BEFORE_MS || 5 * 60 * 1000;
      if (tokenRemaining <= refreshThreshold) {
        if (!isExtendingRef.current) {
          if (process.env.NODE_ENV === 'development') {
            console.log("[SessionTimeout] Cichy auto-refresh access tokena w tle...");
          }
          statesRef.current.handleExtend();
        }
      }
    }, 1000);

    return () => {
      window.removeEventListener('mousemove', recordActivity);
      window.removeEventListener('keydown', recordActivity);
      window.removeEventListener('click', recordActivity);
      window.removeEventListener('scroll', recordActivity);
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  if (!showWarning || !isAuthenticated) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.55)',
      zIndex: 99999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '16px',
        padding: '40px 48px',
        maxWidth: '420px',
        width: '90%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        textAlign: 'center',
        fontFamily: 'Inter, sans-serif',
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
        <h2 style={{
          margin: '0 0 8px 0',
          fontSize: '22px',
          fontWeight: 700,
          color: '#1a1a2e',
        }}>
          Sesja kończy się z powodu braku aktywności
        </h2>
        <p style={{
          margin: '0 0 24px 0',
          color: '#666',
          fontSize: '15px',
          lineHeight: '1.5',
        }}>
          Nie wykryto aktywności przez blisko 2 godziny. Twoja sesja wygaśnie za:
        </p>
        <div style={{
          fontSize: '52px',
          fontWeight: 800,
          color: timeLeft < 60000 ? '#e53e3e' : '#e67e22',
          letterSpacing: '-2px',
          marginBottom: '28px',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {formatTime(timeLeft)}
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            onClick={handleKeepAlive}
            disabled={isExtending}
            style={{
              padding: '12px 28px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: '#2ecc71',
              color: '#fff',
              fontWeight: 700,
              fontSize: '14px',
              cursor: isExtending ? 'not-allowed' : 'pointer',
              opacity: isExtending ? 0.7 : 1,
              transition: 'all 0.2s',
            }}
          >
            {isExtending ? 'Odświeżanie...' : 'Pozostań zalogowany'}
          </button>
          <button
            onClick={() => doLogout(true)}
            style={{
              padding: '12px 28px',
              borderRadius: '10px',
              border: '2px solid #e0e0e0',
              backgroundColor: 'transparent',
              color: '#555',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Wyloguj
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutNotification;
